/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';
import * as jsonp from '../jsonp';
import bidbase from './bidbase';
import bm from '../bidmanager';

/**
 * @module bidders/sovrn
 */
/**
 * @description Implements functionality for Sovrn header bidding
 * @extends _bidbase
 * @requires module:utils
 * @requires module:jsonp
 */
class _sovrnbid extends bidbase {
  _bidRequest: Object;
  constructor() {
    super('sovrn');
    this._bidRequest = {
      id: '',
      impid: {}
    };
    this._mandatoryConfigs = ['bidUrl'];
    this._posId = 'tagid';
    this._isSupported = u.isJSONSupported();
    this._beaconUrlId = 'sBeacon';
  }

  /**
   * Process RTB bid response. Response format:
   * resp.id - bid request id
   * resp.seatbid[0].bid[N].price - ecpm value
   * resp.seatbid[0].bid[N].adm - ad creative
   * resp.seatbid[0].bid[N].nurl - impression pixel
   * @param {Array<string>} posList - List of HB positions in response
   * @param {Object} resp - Response object from back-end
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Object.<string, hbPosData>} - Response after processing
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    resp: any,
    ret: hbPartnerData
  ): hbPartnerData {
    if (!this._isValidRTBResponse(resp) || resp.id !== this._bidRequest.id) {
      return ret;
    }
    resp.seatbid[0].bid.forEach(bid => {
      if (bid.impid) {
        this._bidResponses[bid.impid] = bid;
      }
    });

    posList.forEach(pos => {
      let impid = this._bidRequest.impid[pos];
      let res = this._bidResponses[impid];
      if (res && res.price && res.nurl && res.adm) {
        this._handleBidPosData(
          ret,
          pos,
          res.price,
          impid,
          decodeURIComponent(res.adm),
          res.nurl
        );
      }
    });
    return ret;
  }

  /**
   * get bids for given positions
   * @param {Array<string>} posList - Array of positions to bid
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Promise<Object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};

    //generate unique request id
    this._bidRequest.id = u.getRandom();

    // build params for bid request
    let params = {
      id: this._bidRequest.id,
      site: {
        domain: location.host,
        page: location.pathname
      },
      imp: []
    };
    params.imp = posList.map(pos => {
      // generate unique id for this impresssion (position)
      this._bidRequest.impid[pos] = u.getRandom();
      return {
        id: this._bidRequest.impid[pos],
        banner: {
          w: this._config.posTokenMap[pos].width,
          h: this._config.posTokenMap[pos].height
        },
        tagid: this.getPosKey(pos)
      };
    });

    let reqParams = {
      data: {
        br: JSON.stringify(params)
      },
      timeout: this._config.timeout,
      logPrefix: posList.join(',')
    };
    return jsonp.JSONP(this._config.bidUrl, reqParams).then(
      data => this._processBidResponse(posList, data, ret),
      msg => {
        u.logError(`${this._name} Failed to get bids: ${msg || ''}`);
        return ret;
      }
    );
  }
}

//add bidder to bidmanager
let sovrnbid = new _sovrnbid();
bm.addBidder('sovrn', sovrnbid);
export default sovrnbid;
